import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const Person = ({ p, keep, setKeep, rid }) => (
  <label className={`flex-1 min-w-[14rem] rounded-lg border p-3 text-sm space-y-1 cursor-pointer transition-colors ${keep === p.id ? 'border-primary bg-primary/5' : 'border-border'}`} data-testid={`merge-person-${p.id}`}>
    <span className="flex items-center gap-2"><input type="radio" name={`keep-${rid}`} checked={keep === p.id} onChange={() => setKeep(p.id)} data-testid={`merge-keep-${p.id}`} /><span className="font-medium">{p.full_name}</span></span>
    <span className="block text-xs">Email: {p.email || '—'}</span>
    <span className="block text-xs">Date of birth: {p.date_of_birth || '—'}</span>
    <span className="block text-xs">Director ID: <span className="font-mono">{p.did_masked || '—'}</span> · {p.did_status}</span>
    <span className="block text-xs text-muted-foreground">Companies: {p.companies.join(', ') || '—'}</span>
  </label>
);

const Review = ({ r, onDone }) => {
  const [keep, setKeep] = useState(r.people[0]?.id);
  const decide = async (decision) => {
    if (decision === 'merge' && !window.confirm('Merge these records into the selected person? Their companies and requests move to that person (audited).')) return;
    try { await api.post(`/api/bulk/merge-reviews/${r.id}/decide`, { decision, keep_person_id: keep }); toast.success(decision === 'merge' ? 'Records merged' : 'Kept as separate people'); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="p-3 space-y-2" data-testid={`merge-review-${r.id}`}>
      <p className="text-xs text-muted-foreground">{r.reason} · choose the record to keep</p>
      <div className="flex flex-wrap gap-2">{r.people.map((p) => <Person key={p.id} p={p} keep={keep} setKeep={setKeep} rid={r.id} />)}</div>
      <div className="flex gap-2"><Button size="sm" onClick={() => decide('merge')} data-testid={`merge-confirm-${r.id}`}>Merge into selected</Button>
        <Button size="sm" variant="outline" onClick={() => decide('keep_separate')} data-testid={`merge-separate-${r.id}`}>Keep separate</Button></div>
    </div>
  );
};

export const MergeReviews = () => {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get('/api/bulk/merge-reviews').then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  return (
    <section className="space-y-2" data-testid="merge-reviews">
      <h2 className="font-headings text-lg font-semibold">Possible duplicate directors</h2>
      <p className="text-xs text-muted-foreground">Records are never merged automatically. Compare side by side and decide.</p>
      <div className="divide-y divide-border rounded-lg border border-border">{rows.map((r) => <Review key={r.id} r={r} onDone={load} />)}
        {!rows.length && <p className="p-3 text-sm text-muted-foreground">No possible duplicates to review.</p>}</div>
    </section>
  );
};

export const RetentionCard = () => {
  const [days, setDays] = useState('');
  const [dry, setDry] = useState(null);
  useEffect(() => { api.get('/api/bulk/settings').then((r) => setDays(String(r.data.retention_days))); }, []);
  const saveDays = async () => { try { await api.put('/api/bulk/settings', { retention_days: Number(days) }); toast.success('Retention period saved'); setDry(null); } catch (e) { toast.error(e.response?.data?.detail || 'Owners/admins only'); } };
  const check = async () => { try { setDry((await api.post('/api/bulk/retention/run', null, { params: { dry_run: true } })).data); } catch (e) { toast.error(e.response?.data?.detail || 'Owner only'); } };
  const run = async () => {
    if (!window.confirm(`Permanently delete ${dry.campaigns} completed/cancelled campaign(s) older than ${dry.retention_days} days and their ${dry.recipients} request(s)? Approved Director IDs on people stay.`)) return;
    try { const r = await api.post('/api/bulk/retention/run'); toast.success(`${r.data.campaigns_removed} campaign(s) deleted`); setDry(null); } catch (e) { toast.error(e.response?.data?.detail || 'Owner only'); }
  };
  return (
    <section className="space-y-2" data-testid="retention-card">
      <h2 className="font-headings text-lg font-semibold">Delete old campaign data</h2>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        Keep completed/cancelled campaigns for <Input className="h-8 w-24" type="number" min={30} value={days} onChange={(e) => setDays(e.target.value)} data-testid="retention-days-input" /> days
        <Button size="sm" variant="outline" onClick={saveDays} data-testid="retention-save-btn">Save</Button>
        <Button size="sm" variant="outline" onClick={check} data-testid="retention-check-btn">Check what would be deleted</Button>
        {dry && <Button size="sm" variant="destructive" disabled={!dry.campaigns} onClick={run} data-testid="retention-run-btn">Delete {dry.campaigns} campaign(s)</Button>}
      </div>
      <p className="text-xs text-muted-foreground">Owner only; you'll be asked to re-enter your password. Every run is recorded in the audit log.</p>
    </section>
  );
};
